import { forwardRef } from 'react';
import type { InputProps } from './input.types';
import styles from './input.module.css';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import { useInputStyles } from './use-input-styles';

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            variant = 'default',
            size = 'large',
            iconLeftProps,
            iconRightProps,
            label,
            labelPosition = 'outside',
            error,
            errorProps,
            helperText,
            helperTextProps,
            disabled = false,
            required = false,
            requiredIndicator,
            testID,
            containerClassName,
            inputClassName,
            labelClassName,
            className,
            onBlur,
            onFocus,
            showIf = true,
            ...props
        },
        ref
    ) => {
        const {
            containerClassNames,
            labelOutsideClassNames,
            labelFloatingClassNames,
            inputClassNames,
            iconLeftContainerClassNames,
            iconRightContainerClassNames,
            errorTextClassNames,
            helperTextClassNames,
        } = useInputStyles({
            variant,
            size,
            iconLeftProps,
            iconRightProps,
            error,
            errorProps,
            helperTextProps,
            disabled,
            containerClassName,
            inputClassName,
            labelClassName,
            className,
        });

        if (!showIf) return null;

        return (
            <div className={containerClassNames} data-testid={testID || 'text-input-container'}>
                {label && labelPosition === 'outside' && (
                    <label className={labelOutsideClassNames}>
                        {label}
                        {required && (requiredIndicator ?? <span className={styles.requiredAsterisk}>*</span>)}
                    </label>
                )}

                <div className={styles.inputContainer}>
                    {label && labelPosition === 'floating' && (
                        <label className={labelFloatingClassNames}>
                            {label}
                            {required && (requiredIndicator ?? <span className={styles.requiredAsterisk}>*</span>)}
                        </label>
                    )}
                    <input
                        ref={ref}
                        disabled={disabled}
                        data-testid={testID || 'text-input'}
                        className={inputClassNames}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        {...props}
                    />

                    <Icon
                        showIf={!!iconLeftProps}
                        {...iconLeftProps}
                        size={iconLeftProps?.size}
                        disabled={disabled || iconLeftProps?.disabled}
                        containerClassName={iconLeftContainerClassNames}
                        className={iconLeftProps?.className}
                    />

                    <Icon
                        showIf={!!iconRightProps}
                        {...iconRightProps}
                        size={iconRightProps?.size}
                        disabled={disabled || iconRightProps?.disabled}
                        containerClassName={iconRightContainerClassNames}
                        className={iconRightProps?.className}
                    />
                </div>

                <Text showIf={!!error} {...errorProps} className={errorTextClassNames}>
                    {error}
                </Text>
                <Text showIf={!!helperText && !error} {...helperTextProps} className={helperTextClassNames}>
                    {helperText}
                </Text>
            </div>
        );
    }
);

Input.displayName = 'Input';
